import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Loader2, Upload } from 'lucide-react';
import { receiptsApi } from '../api';
import { MoneyInput } from './MoneyInput';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls =
  'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';

const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
const MAX_BYTES = 10 * 1024 * 1024;

export const RECEIPT_PAYMENT_OPTIONS = [
  { value: 'card', label: 'Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'other', label: 'Other' },
];

// Receipts are ALWAYS uploaded inside a Project (projectId is required).
export const ReceiptUploadDialog = ({ open, onOpenChange, projectId, onUploaded }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [amount, setAmount] = useState('');
  const [purchasedBy, setPurchasedBy] = useState('');
  const [purchasedWhere, setPurchasedWhere] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setAmount('');
    setPurchasedBy('');
    setPurchasedWhere('');
    setPaymentMethod('card');
    setNote('');
  }, [open]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error('File is larger than 10 MB');
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!file) { toast.error('Please choose a receipt photo or PDF'); return; }
    const amt = Number(String(amount).replace(/[^0-9.]/g, ''));
    if (!amount || isNaN(amt) || amt <= 0) { toast.error('Enter the receipt amount'); return; }
    if (!purchasedBy.trim()) { toast.error('Enter who made the purchase'); return; }
    if (!purchasedWhere.trim()) { toast.error('Enter where the purchase was made'); return; }

    setSubmitting(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('project_id', projectId);
    fd.append('amount', String(amt));
    fd.append('purchased_by', purchasedBy.trim());
    fd.append('purchased_where', purchasedWhere.trim());
    fd.append('payment_method', paymentMethod);
    if (note.trim()) fd.append('note', note.trim());
    try {
      const res = await receiptsApi.upload(fd);
      toast.success('Receipt uploaded — costs updated');
      onOpenChange(false);
      onUploaded?.(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not upload receipt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="receipt-upload-dialog"
      >
        <DialogHeader>
          <DialogTitle>Upload Receipt</DialogTitle>
          <DialogDescription className="text-gray-500">
            The amount is added to this project's Materials/Misc costs automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-gray-700 hover:border-[#D4AF37] rounded-lg p-6 text-center transition-colors"
            data-testid="ru-file-picker"
          >
            <Upload size={22} className="mx-auto mb-2 text-[#D4AF37]" />
            {file ? (
              <span className="text-sm text-white break-all">{file.name}</span>
            ) : (
              <span className="text-sm text-gray-400">Tap to choose a photo or PDF (max 10 MB)</span>
            )}
          </button>
          <input ref={fileRef} type="file" accept={ACCEPT} onChange={pick} className="hidden" data-testid="ru-file-input" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">Amount ($) *</label>
              <MoneyInput value={amount} onChange={setAmount} className={inputCls} data-testid="ru-amount" />
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block font-medium">Payment Method *</label>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className={selectCls} data-testid="ru-payment-method">
                {RECEIPT_PAYMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-300 mb-1.5 block font-medium">Who made the purchase? *</label>
            <Input value={purchasedBy} onChange={(e) => setPurchasedBy(e.target.value)} placeholder="e.g., Mike" className={inputCls} data-testid="ru-purchased-by" />
          </div>

          <div>
            <label className="text-sm text-gray-300 mb-1.5 block font-medium">Where was it purchased? *</label>
            <Input value={purchasedWhere} onChange={(e) => setPurchasedWhere(e.target.value)} placeholder="e.g., Home Depot" className={inputCls} data-testid="ru-purchased-where" />
          </div>

          <div>
            <label className="text-sm text-gray-300 mb-1.5 block font-medium">Note (optional)</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g., LVP flooring run" className={inputCls} data-testid="ru-note" />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-3 border-t border-gray-800">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} className="border-gray-700 text-gray-300" data-testid="ru-cancel">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="ru-submit">
            {submitting && <Loader2 size={16} className="animate-spin" />} Upload Receipt
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
